import { format } from 'date-fns';
import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { toNumber } from '../../../../utils/to-number.util';
import { ReverseVestingFormData } from '../event-form.validation';
import { ReverseVestingOnTitle } from './reverse-vesting-on-combobox';

export type ReverseVestingSummaryRowsProps = {
  /**
   * Whether the caller renders more summary rows after this block — attached files, DocuSign docs.
   * Drives the closing rule, which would otherwise hang under the last row of the card.
   */
  hasRowsBelow?: boolean;
  reverseVesting?: ReverseVestingFormData;
  summaryTitleProps?: { titleClassName?: string; titleWrapperClassName?: string };
};

const rowClassName = 'border-b-[1px] border-gray-100';

export const ReverseVestingSummaryRows: FC<ReverseVestingSummaryRowsProps> = ({
  hasRowsBelow,
  reverseVesting,
  summaryTitleProps,
}) => {
  if (!reverseVesting?.enabled) return null;

  const reverseVestedShares = toNumber(reverseVesting.shares);

  const titleProps = {
    ...summaryTitleProps,
    titleClassName: twMerge(summaryTitleProps?.titleClassName, 'whitespace-normal'),
  };

  const monthsValue = (months: number | string | undefined) =>
    months === undefined || months === '' ? (
      '-'
    ) : (
      <AppFormattedMessage id="MONTHS_COUNT" values={{ count: Number(months) }} />
    );

  return (
    <>
      <SummaryItem
        className={rowClassName}
        title={<AppFormattedMessage id="REVERSE_VESTING" />}
        value={<AppFormattedMessage id="ENABLED" />}
        valueClassName="text-forest-600"
        {...titleProps}
      />
      <SummaryItem
        className={rowClassName}
        title={<AppFormattedMessage id="REVERSE_VESTING_SHARES" />}
        value={reverseVestedShares.toLocaleString('en-US')}
        {...titleProps}
      />
      <div
        className={twMerge('flex w-full flex-col gap-3 py-3', hasRowsBelow ? rowClassName : 'pb-0')}
      >
        <SummaryItem
          className="py-0"
          title={<AppFormattedMessage id="VESTING_ON" />}
          value={
            reverseVesting.vestingOn ? (
              <AppFormattedMessage id={ReverseVestingOnTitle[reverseVesting.vestingOn]} />
            ) : (
              '-'
            )
          }
          {...titleProps}
        />
        <SummaryItem
          className="py-0"
          title={<AppFormattedMessage id="DURATION" />}
          value={monthsValue(reverseVesting.durationMonths)}
          {...titleProps}
        />
        <SummaryItem
          className="py-0"
          title={<AppFormattedMessage id="VESTING_FREQ" />}
          value={monthsValue(reverseVesting.frequencyMonths)}
          {...titleProps}
        />
        <SummaryItem
          className="py-0"
          title={<AppFormattedMessage id="CLIFF" />}
          value={monthsValue(reverseVesting.cliffMonths)}
          {...titleProps}
        />
        <SummaryItem
          className="py-0"
          title={<AppFormattedMessage id="START_DATE" />}
          value={reverseVesting.startDate ? format(reverseVesting.startDate, 'dd/MM/yyyy') : '-'}
          {...titleProps}
        />
      </div>
    </>
  );
};
